import type { IDetectMetricResult, IPAMetricKey } from '~/modules/analytics/types';
import type { RawMetricRow } from '~/modules/page-analytic/helpers/pageDetailV2';
import type { IBasePageAnalyticMetric } from '~/modules/page-analytic/types';
import type { EAnalyticColumnKey } from '~/core/gemxql/types';
import type { IBreakdownItem } from '~/core/gemxql/helpers';

import { PAGE_ANALYTIC_MAPPING } from '~/modules/page-analytic';
import { IMetricFormatter } from '~/modules/analytics/types';
import { parseBreakdownItems } from '~/core/gemxql/helpers';

export const getPAMetricValueV2 = (
  metric?: IBasePageAnalyticMetric,
  key?: IPAMetricKey,
): IDetectMetricResult => {
  if (!metric || !key || !(key in PAGE_ANALYTIC_MAPPING))
    return { value: 0, formatter: IMetricFormatter.NORMAL };

  return PAGE_ANALYTIC_MAPPING[key].getMetricValue({ metric });
};

export const calculatePercentageChange = (
  current: number,
  previous: number,
): string | undefined => {
  if (current === 0 && previous === 0) return undefined;
  if (previous === 0) return current > 0 ? '100%' : '0%';
  const change = ((current - previous) / previous) * 100;
  return `${Math.abs(change).toFixed(0)}%`;
};

export const computeDonutData = ({
  currentValue,
  prevValue,
  name,
  hasPreviousData,
}: {
  currentValue: number;
  prevValue: number;
  name: string;
  hasPreviousData: boolean;
}) => ({
  data: [{ key: 'value', value: currentValue }],
  metadata: {
    trend: {
      value: hasPreviousData ? calculatePercentageChange(currentValue, prevValue) : undefined,
      direction: currentValue >= prevValue ? ('upward' as const) : ('downward' as const),
      trend: currentValue >= prevValue ? ('positive' as const) : ('negative' as const),
    },
  },
  name,
});

export const getTotalCountByType = (stats: IBreakdownItem[] | undefined, type: string): number => {
  const item = stats?.find((s) => s?.type?.toLowerCase() === type.toLowerCase());
  return item?.total ?? 0;
};

export const buildBreakdownDonutData = <T extends string>(data: {
  targets: { value: T; label: string }[];
  columnKey: EAnalyticColumnKey;
  totalsRow: RawMetricRow | undefined;
  comparisonTotalsRow: RawMetricRow | undefined;
  sort?: boolean;
}) => {
  const { targets, columnKey, totalsRow, comparisonTotalsRow, sort } = data;
  const items = parseBreakdownItems<T>(totalsRow?.[columnKey]);
  const comparisonItems = parseBreakdownItems<T>(comparisonTotalsRow?.[columnKey]);
  const hasPreviousData = !!comparisonTotalsRow?.[columnKey];
  const result = targets.map(({ value, label }) =>
    computeDonutData({
      currentValue: getTotalCountByType(items, value),
      prevValue: getTotalCountByType(comparisonItems, value),
      name: label,
      hasPreviousData,
    }),
  );
  return sort ? result.sort((a, b) => b.data[0].value - a.data[0].value) : result;
};
